/**
 * Upload Item Component
 *
 * Displays individual upload progress with actions.
 */

import { ExclamationIcon } from '../../../icons/ExclamationIcon';
import { getFileExtension, getMediaTypeFromExtension } from '../../MediaLibrary/utils';
import type { UploadItem } from './types';
import { getFileTypeIcon } from '../../MediaLibrary/utils';
import { CubeIcon } from '../../../icons/CubeIcon';
import { CrossIcon } from '../../../icons';
import { CheckIcon } from '../../../icons/CheckIcon';
import styles from './Uploader.module.scss';

interface UploadItemComponentProps {
  upload: UploadItem;
  onCancel?: (uploadId: string) => void;
  onRetry?: (uploadId: string) => void;
  onRemove?: (uploadId: string) => void;
  onGoToFile?: (assetId: string) => void;
  showGoToFile?: boolean;
  onAddMissingFiles?: (uploadId: string) => void;
}

export const UploadItemComponent = ({
  upload,
  onCancel,
  onRetry,
  onRemove,
  onGoToFile,
  showGoToFile = false,
  onAddMissingFiles,
}: UploadItemComponentProps) => {

  const { id, file, progress, status, error, missingFiles, showAssetLink } = upload;
  const hasMissingFiles = missingFiles && missingFiles.length > 0;

  const goToFile = () => {
    if (onGoToFile && upload.assetId) {
      onGoToFile(upload.assetId);
    }
  };

  const handleLearnMore = () => {
    // TODO: Implement learn more action for upload errors
    alert('Learn more about upload errors.');
  };

  const getTitleIcon = () => {
    if (status === 'error') {
      return (<ExclamationIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)"/>)
    }

    const FileTypeIcon = getFileTypeIcon(getMediaTypeFromExtension(getFileExtension(file.name))) || CubeIcon;
    return <div className={styles.typeIcon}>
      <FileTypeIcon />
    </div> ;
  };

  const renderActions = () => {
    switch (status) {
      case 'uploading':
        if (onCancel) {
          return (
            <div className="flex">
              <span>Uploading...</span>
              <button
                className="btn btn-ghost p0"
                onClick={() => onCancel(id)}
                title="Cancel upload"
              >
                <CrossIcon width={16} height={16} />
              </button>
            </div>
          )
        } else {
          return <span>Uploading...</span>;
        }
      case 'pending':
        return <span>Processing...</span>;
      case 'completed':
        return (
          <>
            <CheckIcon width={16} height={16} className={styles.completedIcon} />
            {showGoToFile && showAssetLink && (
              <button
                type="button"
                aria-label="Go to file"
                title="Go to file"
                onClick={goToFile}
              >Go to file</button>
            )}
          </>
        );
      case 'error':
        return (
          <>
            <span className={styles.errorText}>{error}</span>
            {hasMissingFiles && onAddMissingFiles ? (
              <button
                onClick={() => onAddMissingFiles(id)}
                title="Add missing resource files"
              >Add files</button>
            ) : onRetry ? (
              <button
                onClick={() => onRetry(id)}
                title="Retry upload"
              >Retry</button>
            ) : (
              <button
                onClick={handleLearnMore}
                title="Learn more"
              >Learn more</button>
            )}
          </>
        );
      default:
        break;
    }
  }

  return (
    <div className={`${styles.uploadItem} ${styles[`uploadItem--${status}`]}`}>
      <div className={styles.itemHeader}>
        {getTitleIcon()}
        <div className={styles.itemInfo}>
          <p className={styles.itemName}>{file.name}</p>
          {/* <div className={styles.itemMeta}>
            {formatFileSize(file.size)}
            {status === 'uploading' && progress < 100 && (
              <span className={styles.itemProgressText}> · {progress}%</span>
            )}
            {status === 'error' && error && (
              <span className={styles.errorText}> · {error}</span>
            )}
          </div> */}
        </div>

        <div className={styles.itemActions}>
          {renderActions()}
        </div>
      </div>

      {/* Progress bar */}
      {status === 'uploading' && (
        <div className={styles.progressBar}>
          <div
            className={styles.progressFill}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
};
